/**
 * formattazione.ts — PUNTO UNICO per numeri, valute, date e percentuali.
 *
 * ## Perché esiste
 * Al 28/08/2026 nel progetto c'erano **120 definizioni indipendenti** di funzioni che
 * formattano un numero, **2.727** simboli € cablati in 771 file e **1.032** occorrenze
 * di `'it-IT'` scritte a mano in 589 file. Non è un problema di eleganza: è che quando
 * arriva un cliente svizzero non c'è un posto dove intervenire, ce ne sono 120.
 *
 * ## Il difetto vero che questo file chiude
 * In 93 punti il codice converte a mano ciò che l'utente scrive, e **in 17 di questi
 * cancella i punti** credendoli separatori di migliaia. Quindi chi scrive `1.5` per dire
 * "uno e mezzo" ottiene `15`. Silenziosamente. `parseNumero()` qui sotto lo risolve una
 * volta per tutte, leggendo il numero secondo il locale invece che a intuito.
 *
 * ## Regola d'uso (vincolante)
 * Nessun file dell'app deve chiamare `toLocaleString`, `Intl.NumberFormat`,
 * `toLocaleDateString` o `parseFloat` su testo scritto dall'utente. Si passa da qui.
 * Il motivo è che queste funzioni hanno bisogno di TRE informazioni (locale, valuta,
 * fuso) che il chiamante quasi mai conosce tutte, e che il `ContestoLocale` porta insieme.
 *
 * ## Nota sulle prestazioni
 * Costruire un `Intl.NumberFormat` costa; riusarlo no. Qui sono in cache per chiave,
 * perché queste funzioni girano dentro tabelle con centinaia di righe.
 */

import type { ContestoLocale, Valuta } from './lingue';
import { CONTESTO_ITALIA } from './lingue';

// ============================================================================
// CACHE DEI FORMATTATORI
// ============================================================================

const cacheNumeri = new Map<string, Intl.NumberFormat>();
const cacheDate = new Map<string, Intl.DateTimeFormat>();

function numberFormat(locale: string, opts: Intl.NumberFormatOptions): Intl.NumberFormat {
  const chiave = locale + '|' + JSON.stringify(opts);
  let f = cacheNumeri.get(chiave);
  if (!f) {
    f = new Intl.NumberFormat(locale, opts);
    cacheNumeri.set(chiave, f);
  }
  return f;
}

function dateFormat(locale: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const chiave = locale + '|' + JSON.stringify(opts);
  let f = cacheDate.get(chiave);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, opts);
    cacheDate.set(chiave, f);
  }
  return f;
}

/** Svuota le cache. Serve ai test, che cambiano locale fra un caso e l'altro. */
export function _svuotaCacheFormattatori(): void {
  cacheNumeri.clear();
  cacheDate.clear();
}

// ============================================================================
// NUMERI
// ============================================================================

export interface OpzioniNumero {
  /** Decimali minimi e massimi. Se ne passi uno solo, vale per entrambi. */
  decimali?: number;
  decimaliMin?: number;
  decimaliMax?: number;
  /** Separatore delle migliaia. Default: sì. */
  raggruppa?: boolean;
  /**
   * Notazione compatta, per un asse o un riquadro stretto: «€12.5K», «1,2 Mln».
   *
   * ⚠️ La forma la decide la LINGUA, non chi chiama: l'italiano non accorcia le migliaia
   * (1.234 resta «1.234») e comincia dal milione. Scrivere «1,2k» a mano inventerebbe una
   * convenzione che l'italiano non ha. Senza decimali dichiarati, al più uno («12.5K»).
   */
  compatto?: boolean;
}

/** I decimali di un formato: quelli dichiarati, oppure i predefiniti di chi chiama. Il compatto ne vuole al più uno. */
function decimaliDi(opts: OpzioniNumero, minPredefinito: number, maxPredefinito: (min: number) => number) {
  const min = opts.decimaliMin ?? opts.decimali ?? (opts.compatto ? 0 : minPredefinito);
  const max = opts.decimaliMax ?? opts.decimali ?? (opts.compatto ? Math.max(min, 1) : maxPredefinito(min));
  return { min, max };
}

export function formatNumero(
  valore: number | null | undefined,
  ctx: ContestoLocale = CONTESTO_ITALIA,
  opts: OpzioniNumero = {},
): string {
  if (valore == null || !Number.isFinite(valore)) return '';
  const { min, max } = decimaliDi(opts, 0, (m) => Math.max(m, 2));
  return numberFormat(ctx.localeFormati, {
    minimumFractionDigits: min,
    maximumFractionDigits: max,
    useGrouping: opts.raggruppa ?? true,
    ...(opts.compatto ? { notation: 'compact' as const } : {}),
  }).format(valore);
}

// ============================================================================
// VALUTA
// ============================================================================

export interface OpzioniValuta extends OpzioniNumero {
  /**
   * Sovrascrive la valuta del contesto (un preventivo storico, un ordine al fornitore in
   * dollari o in yuan). Qualunque codice ISO 4217: le `VALUTE` del tenant sono un elenco
   * contro i refusi, non il perimetro del mercato. Un codice inventato fa lanciare `Intl`:
   * chi lo riceve da un utente lo passa prima da `valutaConosciuta`.
   */
  valuta?: Valuta | string;
  /** `symbol` → "1.234,56 €" · `code` → "1.234,56 EUR" · `none` → "1.234,56" */
  stile?: 'symbol' | 'code' | 'none';
}

/**
 * ⚠️ Il PDF NON deve usare `stile: 'symbol'`: il motore jsPDF con i font standard
 * cancella in silenzio i caratteri sopra U+00FF, e il simbolo € è U+20AC.
 * Per i documenti si usa `stile: 'code'` finché non è caricato un font vero.
 * Verificato eseguendo il codice reale: "12.500,00 €" diventa "12.500,00 ".
 */
export function formatValuta(
  valore: number | null | undefined,
  ctx: ContestoLocale = CONTESTO_ITALIA,
  opts: OpzioniValuta = {},
): string {
  if (valore == null || !Number.isFinite(valore)) return '';
  const stile = opts.stile ?? 'symbol';
  const { min, max } = decimaliDi(opts, 2, () => 2);

  if (stile === 'none') {
    return formatNumero(valore, ctx, { decimaliMin: min, decimaliMax: max, raggruppa: opts.raggruppa, compatto: opts.compatto });
  }
  return numberFormat(ctx.localeFormati, {
    style: 'currency',
    currency: opts.valuta ?? ctx.valuta,
    currencyDisplay: stile === 'code' ? 'code' : 'symbol',
    minimumFractionDigits: min,
    maximumFractionDigits: max,
    useGrouping: opts.raggruppa ?? true,
    ...(opts.compatto ? { notation: 'compact' as const } : {}),
  }).format(valore);
}

/**
 * Il SIMBOLO della valuta di chi guarda («€», «$», «£»), per l'etichetta accanto a un campo
 * importo. Un codice senza simbolo nella lingua di chi guarda torna com'è («CHF»).
 */
export function simboloValuta(ctx: ContestoLocale = CONTESTO_ITALIA, valuta?: Valuta | string): string {
  const codice = valuta ?? ctx.valuta;
  try {
    return numberFormat(ctx.localeFormati, { style: 'currency', currency: codice })
      .formatToParts(0)
      .find((p) => p.type === 'currency')?.value ?? codice;
  } catch {
    return codice;
  }
}

/**
 * Un codice ISO 4217 che il motore dei formati sa scrivere («USD», «CNY»). Un codice inventato
 * («XYZ») fa lanciare `Intl`: chi lo chiede all'utente lo verifica qui, chi lo mostra pure.
 */
export function valutaConosciuta(codice: string | null | undefined): boolean {
  if (!codice || !/^[A-Z]{3}$/.test(codice)) return false;
  try { numberFormat('en', { style: 'currency', currency: codice }); return true; } catch { return false; }
}

/**
 * Un fuso orario IANA che il motore dei formati conosce («Europe/Rome»). Uno inventato fa lanciare `Intl`: chi lo
 * chiede all'utente (il fuso dell'assistente virtuale, atlante cap. 264) lo verifica qui, come il server.
 */
export function fusoOrarioConosciuto(fuso: string | null | undefined): boolean {
  if (!fuso || !fuso.trim()) return false;
  try { dateFormat('en', { timeZone: fuso.trim() }); return true; } catch { return false; }
}

// ============================================================================
// PERCENTUALI
// ============================================================================

/** `valore` è la percentuale già in centesimi: 22 → "22%", non 0,22. */
export function formatPercentuale(
  valore: number | null | undefined,
  ctx: ContestoLocale = CONTESTO_ITALIA,
  // Una percentuale non si accorcia: «12K %» non esiste. Il tipo lo impedisce invece di ignorarlo.
  opts: Omit<OpzioniNumero, 'compatto'> = {},
): string {
  if (valore == null || !Number.isFinite(valore)) return '';
  const min = opts.decimaliMin ?? opts.decimali ?? 0;
  const max = opts.decimaliMax ?? opts.decimali ?? 2;
  return numberFormat(ctx.localeFormati, {
    style: 'percent',
    minimumFractionDigits: min,
    maximumFractionDigits: max,
  }).format(valore / 100);
}

// ============================================================================
// LETTURA DI CIÒ CHE SCRIVE L'UTENTE  (il difetto vero)
// ============================================================================

/**
 * Legge un numero scritto a mano, secondo le convenzioni del locale.
 *
 * Il problema che risolve, con un esempio reale: in italiano `1.234,56` significa
 * milleduecentotrentaquattro e cinquantasei. In inglese lo stesso testo significa
 * uno virgola duecentotrentaquattro. Chi cancella i punti "perché sono le migliaia"
 * sbaglia in un caso su due, e non se ne accorge perché il risultato è comunque un
 * numero valido.
 *
 * Strategia: si guarda QUALE separatore compare per ULTIMO. Quello è il decimale.
 * È l'unica euristica che regge sia su `1.234,56` sia su `1,234.56` sia su `1234,5`.
 * Se compare un separatore solo e non c'è ambiguità, si usa il locale per decidere.
 *
 * @returns il numero, oppure `null` se il testo non è un numero (MAI 0: lo zero è un
 *          valore legittimo e confonderlo con "vuoto" è un altro difetto classico).
 */
export function parseNumero(
  testo: string | number | null | undefined,
  ctx: ContestoLocale = CONTESTO_ITALIA,
): number | null {
  if (typeof testo === 'number') return Number.isFinite(testo) ? testo : null;
  if (testo == null) return null;

  let s = String(testo).trim();
  if (s === '') return null;

  // Via tutto ciò che non è cifra, separatore o segno: simboli di valuta, spazi
  // (compreso quello unificatore che Intl inserisce), lettere.
  s = s.replace(/[^\d.,\-+]/g, '');
  if (s === '' || s === '-' || s === '+') return null;

  const ultimaVirgola = s.lastIndexOf(',');
  const ultimoPunto = s.lastIndexOf('.');

  let decimale: '.' | ',' | null = null;
  if (ultimaVirgola >= 0 && ultimoPunto >= 0) {
    // Ci sono entrambi: l'ultimo è il decimale, l'altro è raggruppamento.
    decimale = ultimaVirgola > ultimoPunto ? ',' : '.';
  } else if (ultimaVirgola >= 0 || ultimoPunto >= 0) {
    const sep = ultimaVirgola >= 0 ? ',' : '.';
    const pos = ultimaVirgola >= 0 ? ultimaVirgola : ultimoPunto;
    const cifreDopo = s.length - pos - 1;
    const occorrenze = s.split(sep).length - 1;
    if (occorrenze > 1) {
      // `1.234.567` → più occorrenze dello stesso separatore: è raggruppamento.
      decimale = null;
    } else if (cifreDopo === 3 && !/^[-+]?0/.test(s)) {
      // Ambiguo: `1.234` può essere 1234 o 1,234. Decide il locale.
      const sepLocale = separatoreDecimaleDi(ctx.localeFormati);
      decimale = sep === sepLocale ? sep : null;
    } else {
      decimale = sep;
    }
  }

  if (decimale === null) {
    s = s.replace(/[.,]/g, '');
  } else {
    const raggruppamento = decimale === ',' ? '.' : ',';
    s = s.split(raggruppamento).join('');
    s = s.replace(decimale, '.');
  }

  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Il separatore decimale di un locale, chiesto a Intl invece che indovinato. */
export function separatoreDecimaleDi(locale: string): '.' | ',' {
  const parti = numberFormat(locale, { minimumFractionDigits: 1 }).formatToParts(1.1);
  const d = parti.find((p) => p.type === 'decimal')?.value;
  return d === ',' ? ',' : '.';
}

/**
 * Leggere e scrivere numeri nella lingua di `ctx`, nella forma che chiede il codice condiviso del
 * cantiere (`NumeriNelLocale` in `shared/cantiere/regole/valoriDato.ts`).
 *
 * Quel codice gira anche in YumaWORK web e non può importare da qui: riceve queste due funzioni
 * invece di riscriversi le regole sui separatori. Restano UNA, in questo file.
 */
export function numeriNelLocale(ctx: ContestoLocale = CONTESTO_ITALIA): {
  leggi(testo: string): number | null;
  scrivi(valore: number, opzioni: OpzioniNumero): string;
} {
  return {
    leggi: (testo) => parseNumero(testo, ctx),
    scrivi: (valore, opzioni) => formatNumero(valore, ctx, opzioni),
  };
}

// ============================================================================
// DATE
// ============================================================================

export type StileData = 'breve' | 'media' | 'lunga' | 'mese' | 'soloMese' | 'soloMeseBreve' | 'oraSola' | 'dataOra' | 'giornoBreve' | 'giornoMese' | 'giornoMeseBreve';

const STILI: Record<StileData, Intl.DateTimeFormatOptions> = {
  breve: { day: '2-digit', month: '2-digit', year: 'numeric' },
  media: { day: 'numeric', month: 'short', year: 'numeric' },
  lunga: { day: 'numeric', month: 'long', year: 'numeric' },
  /** «settembre 2026»: il mese di competenza di un compenso, senza giorno. */
  mese: { month: 'long', year: 'numeric' },
  /** «agosto»: un mese senza anno (il mese del giorno di progetto di un luogo, che vale per tutti gli anni). */
  soloMese: { month: 'long' },
  /** «ott»: un mese in poco spazio (le colonne della produzione mese per mese nel PDF della simulazione). */
  soloMeseBreve: { month: 'short' },
  oraSola: { hour: '2-digit', minute: '2-digit' },
  dataOra: { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' },
  /** «ven 9/10»: un giorno vicino, senza anno (la scadenza di un sollecito che non cade oggi). */
  giornoBreve: { weekday: 'short', day: 'numeric', month: 'numeric' },
  /** «21 luglio»: un giorno senza anno (il giorno di un anno meteorologico tipo, che non ha anno). */
  giornoMese: { day: 'numeric', month: 'long' },
  /** «12 ott»: un giorno vicino in poco spazio (la scadenza nel riquadro di una gara). */
  giornoMeseBreve: { day: 'numeric', month: 'short' },
};

/**
 * ⚠️ Il fuso orario è SEMPRE esplicito. Senza, il browser usa quello del computer:
 * un amministratore che guarda da Londra vedrebbe gli appuntamenti sfasati di un'ora.
 * Lo stesso difetto esiste oggi in 15 lavori automatici su 87 che non lo dichiarano.
 */
export function formatData(
  data: Date | string | number | null | undefined,
  ctx: ContestoLocale = CONTESTO_ITALIA,
  stile: StileData = 'breve',
): string {
  const d = aData(data);
  if (!d) return '';
  return dateFormat(ctx.localeFormati, { ...STILI[stile], timeZone: ctx.fusoOrario }).format(d);
}

/**
 * «settembre 2026» da una chiave mese `AAAA-MM` (il mese di competenza di un compenso).
 * La chiave è già nel fuso dell'azienda: si formatta il primo del mese in UTC così nessun
 * fuso può farlo scivolare al mese prima.
 */
export function formatMese(chiave: string, ctx: ContestoLocale = CONTESTO_ITALIA): string {
  const m = /^(\d{4})-(\d{2})$/.exec(chiave);
  if (!m) return chiave;
  return dateFormat(ctx.localeFormati, { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
    new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)),
  );
}

/**
 * «set» da una chiave mese `AAAA-MM`: la sigla per un asse dove i mesi sono dodici
 * in fila e il nome intero non ci sta. Stessa cautela di `formatMese` sul fuso.
 */
export function formatMeseBreve(chiave: string, ctx: ContestoLocale = CONTESTO_ITALIA): string {
  const m = /^(\d{4})-(\d{2})$/.exec(chiave);
  if (!m) return chiave;
  return dateFormat(ctx.localeFormati, { month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1)))
    // Alcune lingue chiudono la sigla con un punto («set.»): sull'asse è rumore.
    .replace(/\.$/, '');
}

/**
 * Come si scrive un GIORNO: la data piena, o il giorno della settimana con giorno e mese
 * («lun 14 set»), o il solo giorno della settimana («lun»).
 */
export type StileGiorno = 'breve' | 'media' | 'lunga' | 'settimana' | 'nomeGiorno' | 'giornoBreve' | 'giornoLungo';

const STILI_GIORNO: Record<StileGiorno, Intl.DateTimeFormatOptions> = {
  breve: STILI.breve,
  media: STILI.media,
  lunga: STILI.lunga,
  settimana: { weekday: 'short', day: 'numeric', month: 'short' },
  nomeGiorno: { weekday: 'short' },
  /** «lun 2»: un giorno in un elenco dove il mese è già nell'intestazione (le ore del mese di YumaWORK). */
  giornoBreve: { weekday: 'short', day: 'numeric' },
  /** «martedì 3 novembre»: oggi, detto per intero (la scheda della timbratura in «La mia giornata» di YumaWORK). */
  giornoLungo: { weekday: 'long', day: 'numeric', month: 'long' },
};

/**
 * Un giorno `AAAA-MM-GG` scritto per chi legge.
 *
 * ⚠️ La chiave è GIÀ il giorno civile nel fuso dell'azienda (la decide chi la calcola, di
 * solito il server): non è un istante. Si formatta in UTC, come `formatMese`, così nessun
 * fuso di chi guarda può farla scivolare al giorno prima. Passarla a `formatData` la farebbe
 * leggere come mezzanotte UTC, e da Los Angeles il 14 diventerebbe il 13.
 * Una chiave che non è un giorno torna com'è: meglio leggerla grezza che non leggerla.
 *
 * Un ISTANTE passato al posto del giorno (`2026-10-25T11:54:42.850Z`, 25/10/2026: il riquadro dell'ordine di lavoro
 * lo mostrava così) si scrive come il giorno in cui cade per chi guarda, nel suo fuso: è la stessa data che
 * `formatData` darebbe, e non c'è niente da far scivolare, perché un istante il suo giorno lo ha già.
 */
export function formatGiorno(
  giorno: string,
  ctx: ContestoLocale = CONTESTO_ITALIA,
  stile: StileGiorno = 'breve',
): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(giorno);
  if (!m) {
    const istante = /^\d{4}-\d{2}-\d{2}T/.test(giorno) ? new Date(giorno) : null;
    if (!istante || Number.isNaN(istante.getTime())) return giorno;
    return dateFormat(ctx.localeFormati, { ...STILI_GIORNO[stile], timeZone: ctx.fusoOrario }).format(istante).replace(/\.$/, '');
  }
  return dateFormat(ctx.localeFormati, { ...STILI_GIORNO[stile], timeZone: 'UTC' })
    .format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))))
    // «lun.» in alcune lingue: in una striscia di giorni il punto è rumore.
    .replace(/\.$/, '');
}

/** Dove cade un giorno rispetto a oggi: le quattro forme di «oggi alle 9:12», «ieri alle 18:30»… */
export type GiornoRelativo = 'oggi' | 'ieri' | 'settimana' | 'prima';

/**
 * Un istante detto rispetto a oggi, nel fuso di chi guarda: i PEZZI, non la frase.
 *
 * ## Perché i pezzi e non la frase
 * Perché la frase («ieri alle 18:30», «yesterday at 6:30 PM», «lunedì alle 21:05») la compone
 * chi chiama con `t()`: l'ordine delle parole cambia da una lingua all'altra, i numeri e i nomi
 * dei giorni no. Qui si decide soltanto QUALE giorno è, contando i giorni di calendario nel
 * fuso dell'azienda (un'apertura alle 00:30 di Roma è «oggi», anche se in UTC era ieri).
 */
export interface MomentoRelativo {
  giorno: GiornoRelativo;
  /** «18:30» */
  ora: string;
  /** «lunedì»: serve alla forma `settimana` (da 2 a 6 giorni fa). */
  nomeGiorno: string;
  /** «3 set 2026»: serve alla forma `prima`. */
  data: string;
}

/** Il numero del giorno di calendario (giorni dal 1970) nel fuso indicato. */
function numeroGiorno(d: Date, fuso: string): number {
  const parti = dateFormat('en-US', {
    timeZone: fuso,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const valore = (tipo: string) => Number(parti.find((p) => p.type === tipo)?.value ?? NaN);
  return Math.round(Date.UTC(valore('year'), valore('month') - 1, valore('day')) / 86_400_000);
}

export function momentoRelativo(
  data: Date | string | number | null | undefined,
  ctx: ContestoLocale = CONTESTO_ITALIA,
  adesso: Date = new Date(),
): MomentoRelativo | null {
  const d = aData(data);
  if (!d) return null;
  const distanza = numeroGiorno(adesso, ctx.fusoOrario) - numeroGiorno(d, ctx.fusoOrario);
  // Un orologio avanti di qualche secondo non deve produrre «domani»: il futuro vicino è oggi.
  const giorno: GiornoRelativo =
    distanza <= 0 ? 'oggi' : distanza === 1 ? 'ieri' : distanza < 7 ? 'settimana' : 'prima';
  return {
    giorno,
    ora: formatData(d, ctx, 'oraSola'),
    nomeGiorno: dateFormat(ctx.localeFormati, { weekday: 'long', timeZone: ctx.fusoOrario }).format(d),
    data: formatData(d, ctx, 'media'),
  };
}

/**
 * Quanti giorni di CALENDARIO separano oggi da un istante, nel fuso dell'azienda: 0 = oggi, 1 = domani, -1 = ieri.
 * Non le ore divise per 24: alle 19:30 una scadenza di domani alle 10:00 è fra 14 ore, ma è «domani» (11/10/2026: il
 * Radar delle gare la diceva «oggi», e Simone se n'è accorto confrontando col termine scritto nel dettaglio).
 */
export function giorniDiCalendarioDaOggi(
  data: Date | string | number | null | undefined,
  ctx: ContestoLocale = CONTESTO_ITALIA,
  adesso: Date = new Date(),
): number | null {
  const d = aData(data);
  if (!d) return null;
  return numeroGiorno(d, ctx.fusoOrario) - numeroGiorno(adesso, ctx.fusoOrario);
}

function aData(v: Date | string | number | null | undefined): Date | null {
  if (v == null) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  // Timestamp Firestore: ha `toDate()`. Non importiamo firebase qui apposta.
  if (typeof v === 'object' && typeof (v as { toDate?: unknown }).toDate === 'function') {
    const d = (v as { toDate: () => Date }).toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

// ============================================================================
// ELENCHI E ORDINAMENTO
// ============================================================================

/**
 * "Mario, Anna e Luca" in italiano · "Mario, Anna and Luca" in inglese. Con `oppure`: "7,6 o 10" · "7.6 or 10", per
 * le alternative fra cui scegliere.
 */
export function formatElenco(voci: readonly string[], ctx: ContestoLocale = CONTESTO_ITALIA, opzioni: { oppure?: boolean } = {}): string {
  if (voci.length === 0) return '';
  return new Intl.ListFormat(ctx.localeFormati, { style: 'long', type: opzioni.oppure ? 'disjunction' : 'conjunction' }).format(voci);
}

/**
 * Confronto per ordinare nomi. `localeCompare` senza locale ordina male gli accenti
 * e mette le maiuscole prima delle minuscole.
 */
export function confrontaTesti(a: string, b: string, ctx: ContestoLocale = CONTESTO_ITALIA): number {
  return a.localeCompare(b, ctx.localeFormati, { sensitivity: 'base', numeric: true });
}
